import { useParams } from 'react-router-dom';

import { Button } from '@atoms';
import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { errorMessage, successMessage } from '@services';
import React, { useEffect, useRef, useState } from 'react';
import UploadCSVPart from './UploadCSV.part';
import CSVTablePart from './CSVTable.part';
import { CSVData, formatErrorWithIndexes } from './utils';
import { useUploadCSVLink } from 'src/features/Enterprise/LinksManagement/api/upload-csv';

interface IUploadCSVModalProps {
  open: boolean;
  onClose: () => void;
}

const UploadCSVModal: React.FC<IUploadCSVModalProps> = ({ open, onClose }) => {
  const { id: organizationId } = useParams<{ id: string }>();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);

  const inputRef = useRef<any>(null);
  const errorMessagesRef = useRef<string[]>([]);

  useEffect(() => {
    if (uploadSuccess) {
      setUploadSuccess(false);
    }
  }, [uploadSuccess]);

  const uploadCSVLinkMutation = useUploadCSVLink({
    mutationConfig: {
      onSuccess: () => {
        successMessage(
          `CSV file has been uploaded!\nIt would take minutes to save successfully\nPlease reload after minutes if you don't see the updates`,
        );
        setUploadSuccess(true);
        onClose();
      },
      onError: () => {
        onClose();
        setUploadSuccess(true);
        errorMessage('CSV file upload failed!');
      },
    },
  });

  const handleUploadCSV = async (file: File) => {
    if (!organizationId) {
      return;
    }

    uploadCSVLinkMutation.mutate({
      data: file,
      organizationId: organizationId,
    });
  };

  const readFile = async (file: Blob) => {
    const reader = new FileReader();
    reader.readAsText(file);
    return new Promise((resolve, reject) => {
      reader.onloadend = (e: any) => resolve(e.target.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const parseCSVLine = (line: string) => {
    // Parse a single line of CSV, handling quoted fields
    return [...line.matchAll(/(?:^|,)(?:"((?:[^"]|"")*)"|([^,]*))/g)].map((m) =>
      m[1] !== undefined ? m[1].replace(/""/g, '"') : m[2],
    );
  };

  const parseCSV = (text: string): any[] => {
    const lines = text.split('\n');
    const result = lines.map((line) => parseCSVLine(line.trim().replaceAll('\r', '')));

    return result;
  };

  const _validateHeadersCSV = (csvData: any): boolean => {
    const headers = csvData[0];

    const requiredHeaders = [
      'url',
      'category',
      'asset_title',
      'priority',
      'status',
      'identified_type',
    ];
    const missingHeaders = requiredHeaders.filter((header) => !headers.includes(header));
    if (missingHeaders.length) {
      errorMessagesRef.current.push(`Missing required headers: ${missingHeaders.join(', ')}`);
      return false;
    }
    return true;
  };

  const _formatCsvData = (csvData: any): any[] => {
    const data = csvData.slice(1);
    try {
      const result: any[] = data.map((item: any, index: number) => ({
        url: item[0] || null,
        category: item[2] || null,
        asset_title: item[3] || null,
        priority: item[4] || null,
        status: item[5] || null,
        identified_type: item[8] || null,
        index,
      }));
      return result;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const _validateInvalidFormatData = (validateDataCSV: CSVData[]): CSVData[] => {
    const invalidIndexes: number[] = [];
    const result: CSVData[] = [];
    validateDataCSV.forEach((data) => {
      if (
        !data.url ||
        !data.category ||
        !data.asset_title ||
        !data.priority ||
        !data.status ||
        !data.identified_type
      ) {
        invalidIndexes.push(data.index);
      } else {
        result.push(data);
      }
    });

    if (!validateDataCSV.length) {
      errorMessagesRef.current.push('There is an error in the input format.');
    }

    if (invalidIndexes.length) {
      errorMessagesRef.current.push(
        formatErrorWithIndexes('Invalid input format.', invalidIndexes),
      );
    }

    return result;
  };

  const _validateData = (data: CSVData[]): CSVData[] => {
    let validatedData = [...data];
    validatedData = _validateInvalidFormatData(validatedData);

    return validatedData;
  };

  const handleFileChange = async (e: any) => {
    if (!e.target.files.length) {
      return;
    }

    const inputFile = e.target.files[0];
    const fileExtension = inputFile?.type.split('/')[1];

    if (!['csv'].includes(fileExtension)) {
      errorMessage('Please upload a valid CSV file. Supported formats: .csv');
      e.target.value = '';
      return;
    }
    try {
      /* Read file */
      const fileData = await readFile(inputFile);
      const parsedData = parseCSV(fileData as string);
      const validateHeaders = _validateHeadersCSV(parsedData);
      if (!validateHeaders) {
        e.target.value = '';
        errorMessage(errorMessagesRef.current.join(' '));
        errorMessagesRef.current = [];
        return;
      }
      const formattedData = _formatCsvData(parsedData);
      const validatedData = _validateData(formattedData);
      if (errorMessagesRef.current.length > 0) {
        e.target.value = '';
        errorMessage(errorMessagesRef.current.join(' '));
        errorMessagesRef.current = [];
        return;
      }
      setSelectedFile(inputFile);
      setDataTable(
        validatedData.map((item, index) => ({
          ...item,
          id: index + 1,
        })),
      );
    } catch (error: any) {
      errorMessage('Error reading file. Please try again.');
      console.error(error);
    }

    e.target.value = '';
  };

  const handleCancel = () => {
    setDataTable([]);
    setSelectedFile(null);
    onClose();
  };

  const handleUpload = () => {
    if (!selectedFile) {
      errorMessage('Please select a file to upload.');
      return;
    }
    handleUploadCSV(selectedFile);
  };

  useEffect(() => {
    if (uploadSuccess) {
      setDataTable([]);
      setSelectedFile(null);
    }
  }, [uploadSuccess]);

  const onClickImport = () => handleUpload();

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      PaperProps={{
        sx: {
          borderRadius: '10px',
          minWidth: '913px',
          height: '458px',
          position: 'relative',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="file-upload"
          multiple={false}
          ref={inputRef}
        />
        {dataTable.length ? (
          <CSVTablePart
            loading={uploadCSVLinkMutation.isLoading}
            inputRef={inputRef}
            dataTable={dataTable}
          />
        ) : (
          <UploadCSVPart handleFileChange={handleFileChange} onCancel={onClose} />
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          color="inherit"
          onClick={handleCancel}
          sx={{
            width: 'fit-content',
            paddingX: '10px',
            borderRadius: '10px',
            marginTop: '8px',
            marginBottom: '12px',
          }}
        >
          <Box component="div" display="flex" gap={1.25}>
            <Typography component="span" color="text.white" variant="body2">
              Cancel
            </Typography>
          </Box>
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onClickImport}
          sx={{
            width: 'fit-content',
            paddingX: '30px',
            paddingY: '10px',
            borderRadius: '10px',
            marginTop: '8px',
            marginBottom: '12px',
          }}
          disabled={!selectedFile}
        >
          <Typography component="span" color="text.white" variant="body2">
            Import
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadCSVModal;
